import { camelize } from "inflected";
import { isEqual } from "lodash";
import validate from "validate.js";
import { UnavailableEnvironmentIndexError } from "../../IndexEntry";
import { RichString } from "../../RichString";
import type { GlobalAction } from "../../database/behaviour/GlobalAction";
import { getEnvironmentIndex } from "../../getEnvironmentIndex";
import { getAppSettings, getGeneratingRoot, getNamespacedApiIdentifier } from "../../utils";
import type { ComputedView } from "../../database/ComputedView";
import type { BaseOptions } from "../Constraints";

validate.validators.noModelNamespaceCollision = <T extends GlobalAction | ComputedView>(
  namespacedApiIdentifier: string,
  options: BaseOptions<T> & { subject: "Action" | "View" },
  key: keyof T & string,
  node: T
) => {
  try {
    if (!getAppSettings(node).frameworkFeatures.supportNamespace) return;
  } catch (error) {
    return;
  }

  let index;
  try {
    index = getEnvironmentIndex(getGeneratingRoot(node));
  } catch (e) {
    if (e instanceof UnavailableEnvironmentIndexError) {
      return; // don't validate uniqueness when the environment index is unavailable
    } else {
      throw e;
    }
  }

  for (const model of index.modelsWithoutProblemsData) {
    // Check for collisions with model namespace
    for (let i = 1; i <= model.namespace.length; i++) {
      const currentModelNamespace = model.namespace.slice(0, i).join("/");
      if (currentModelNamespace === namespacedApiIdentifier) {
        return (value: string) =>
          `^${options.subject} name ${RichString.text(value)} collides with model namespace ${RichString.text(currentModelNamespace)}`;
      }
    }

    const modelNamespacedApiId = getNamespacedApiIdentifier(model.namespace, model.apiIdentifier);

    // Check for collisions with node namespace
    for (let i = 1; i <= node.namespace.length; i++) {
      const current = node.namespace.slice(0, i).join("/");
      if (current === modelNamespacedApiId) {
        return (value: string) =>
          `^${options.subject} namespace ${RichString.text(current)} collides with model ${RichString.text(modelNamespacedApiId)}`;
      }
    }

    // Check for combined namespace collisions with models. Ex: Model `a/b/c` and action/view `aB/c`
    if (!isEqual(model.namespace, node.namespace) && isEqual(camelize(model.namespace.join("_")), camelize(node.namespace.join("_")))) {
      return (value: string) =>
        `^${options.subject} namespace ${RichString.text(value)} collides with model ${RichString.text(modelNamespacedApiId)}`;
    }

    // Check for actions/views with identical namespaces and apiIDs as models
    if (modelNamespacedApiId === namespacedApiIdentifier) {
      return (value: string) =>
        `^${options.subject} name ${RichString.text(value)} collides with model ${RichString.text(modelNamespacedApiId)}`;
    }
  }
};
